'use client'

import type { InputProps } from '@fukku/input/types'
import textBodyL from '@fukku/styles/texts/textBodyL.module.css'
import textBodyM from '@fukku/styles/texts/textBodyM.module.css'
import textLabelS from '@fukku/styles/texts/textLabelS.module.css'
import cx from 'classix'
import {
	type ChangeEvent,
	type FocusEvent,
	type ReactNode,
	useEffect,
	useRef,
	useState,
} from 'react'
import { applyStylesIf } from 'utils/applyStylesIf/applyStylesIf'

import { ClearOptionButton } from './components/ClearOptionButton'
import { ErrorMessage } from './components/ErrorMessage'
import { HintMessage } from './components/HintMessage'
import { PlaceholderSrOnly } from './components/PlaceholderSrOnly'

import styles from './Input.module.css'

export function Input({
	children,
	disabled = false,
	errorMessage,
	errorMessageDataTestId,
	errorPlaceholders,
	hintMessage,
	hintPlaceholders,
	id,
	label,
	labelFocused = label,
	ariaLabelClearOption = '',
	name = id,
	onBlur = () => {
		/* do nothing */
	},
	onChange = () => {
		/* do nothing */
	},
	onFocus = () => {
		/* do nothing */
	},
	onInput = () => {
		/* do nothing */
	},
	placeholder = '',
	type = 'text',
	value = '',
	forceHint = false,
	showAsTextArea = false,
	withClearOption = false,
	className,
	refCallback = () => {},
	...inputAttributes
}: Readonly<InputProps>): ReactNode {
	const [inputValue, setInputValue] = useState<InputProps['value']>(value)
	const [focused, setFocused] = useState<boolean>(false)
	const [showHint, setShowHint] = useState<boolean>(forceHint)
	const inputRef = useRef<(HTMLTextAreaElement & HTMLInputElement) | null>(null)
	const inputRefCallback = (
		event: HTMLTextAreaElement & HTMLInputElement
	): void => {
		refCallback(event)
		inputRef.current = event
	}
	const Component = showAsTextArea ? 'textarea' : 'input'
	const isFilled = inputValue !== ''
	const isFocusedOrFilled = focused || isFilled
	const showClearOption = withClearOption && inputValue !== ''
	const errorMessageId = `${id}-error`
	const hintMessageId = `${id}-hint`

	useEffect(() => {
		setInputValue(value)
	}, [value])

	const handleClear = (): void => {
		setInputValue('')

		if (inputRef.current) {
			inputRef.current.focus()
		}
	}

	const handleChange = (
		e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
	): void => {
		const { target } = e
		setInputValue(target.value)
		onChange(target.value)
	}

	const handleFocus = (
		e: FocusEvent<HTMLTextAreaElement | HTMLInputElement>
	): void => {
		setFocused(true)
		if (hintMessage) {
			setShowHint(true)
		}
		onFocus(e)
	}

	const handleBlur = (
		e: FocusEvent<HTMLTextAreaElement | HTMLInputElement>
	): void => {
		setFocused(false)
		if (hintMessage) {
			setShowHint(false)
		}
		onBlur(e)
	}

	const getDescriberId = (): string | undefined => {
		if (errorMessage) {
			return errorMessageId
		}

		if (showHint) {
			return hintMessageId
		}

		return undefined
	}

	return (
		<div className={styles.inputWrapper}>
			<div
				className={cx(
					styles.box,
					applyStylesIf(isFocusedOrFilled, styles.focused),
					applyStylesIf(!!errorMessage, styles.error),
					applyStylesIf(disabled, styles.disabled),
					className
				)}
			>
				<label
					className={cx(
						styles.label,
						applyStylesIf(isFocusedOrFilled, styles.focusedLabel),
						isFocusedOrFilled ? textLabelS.className : textBodyM.className
					)}
					htmlFor={id}
				>
					{isFocusedOrFilled ? labelFocused : label}
				</label>
				<Component
					aria-describedby={getDescriberId()}
					aria-invalid={!!errorMessage}
					className={cx(
						styles.input,
						textBodyL.className,
						applyStylesIf(isFocusedOrFilled, styles.focusedInput),
						applyStylesIf(showClearOption, styles.content)
					)}
					readOnly={disabled}
					disabled={disabled}
					id={id}
					name={name}
					onBlur={handleBlur}
					onChange={handleChange}
					onFocus={handleFocus}
					onInput={onInput}
					placeholder={placeholder}
					type={type}
					value={inputValue}
					ref={inputRefCallback}
					{...inputAttributes}
				/>
				<ClearOptionButton
					className={styles.clearButton}
					ariaLabelClearOption={ariaLabelClearOption}
					onClear={handleClear}
					shouldRender={showClearOption}
				/>
				{children}
			</div>
			<PlaceholderSrOnly placeholder={placeholder} />
			<HintMessage
				id={hintMessageId}
				hintPlaceholders={hintPlaceholders}
				shouldRender={showHint && !errorMessage}
			>
				{hintMessage}
			</HintMessage>
			<ErrorMessage
				errorMessageId={errorMessageId}
				errorPlaceholders={errorPlaceholders}
				data-testid={errorMessageDataTestId}
			>
				{errorMessage}
			</ErrorMessage>
		</div>
	)
}
